import { ElMessage } from 'element-plus'
import { importLocalPackingLists } from '@/api/packingLists'

export const PACKING_BATCH_STORAGE_KEY = 'packing_batches_v1'
export const PACKING_BATCH_IMPORTED_KEY = 'packing_batches_imported_v1'

let _importInFlight = null

function readLocalBatches() {
  try {
    const raw = localStorage.getItem(PACKING_BATCH_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function markImported() {
  try {
    localStorage.setItem(PACKING_BATCH_IMPORTED_KEY, new Date().toISOString())
    localStorage.removeItem(PACKING_BATCH_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

/**
 * 更新后自动导入本机旧包装单。同一浏览器只成功跑一轮。
 * @returns {Promise<{ran:boolean, imported:number, skipped:number}|null>}
 */
export async function tryImportLocalPackingBatches({ notify = true } = {}) {
  if (typeof window === 'undefined') return null
  try {
    if (localStorage.getItem(PACKING_BATCH_IMPORTED_KEY)) return { ran: false, imported: 0, skipped: 0 }
  } catch {
    /* ignore */
  }

  const batches = readLocalBatches()
  if (!batches.length) {
    try {
      localStorage.setItem(PACKING_BATCH_IMPORTED_KEY, new Date().toISOString())
    } catch {
      /* ignore */
    }
    return { ran: false, imported: 0, skipped: 0 }
  }

  if (_importInFlight) return _importInFlight

  _importInFlight = (async () => {
    try {
      const res = await importLocalPackingLists(batches)
      if (!res?.success) {
        if (notify) {
          ElMessage.warning(res?.message || '本机包装单自动导入未完成，将在下次打开时重试')
        }
        return { ran: true, imported: 0, skipped: 0, failed: true }
      }
      const imported = Number(res.data?.imported_count || 0)
      const skipped = Number(res.data?.skipped_count || 0)
      const failed = Number(res.data?.failed_count || 0)
      // 全部处理完（含跳过）才清本地；若仍有失败项则保留本机数据重试
      if (failed <= 0) {
        markImported()
      } else if (imported + skipped > 0) {
        // 部分成功：去掉已导入/已跳过的，保留失败项
        const doneIds = new Set(
          [...(res.data?.imported || []), ...(res.data?.skipped || [])]
            .map((x) => String(x.legacy_client_batch_id || ''))
            .filter(Boolean)
        )
        const remain = batches.filter((b) => !doneIds.has(String(b.batch_id || '')))
        try {
          if (remain.length) {
            localStorage.setItem(PACKING_BATCH_STORAGE_KEY, JSON.stringify(remain))
          } else {
            markImported()
          }
        } catch {
          /* ignore */
        }
      }
      if (notify && imported > 0) {
        ElMessage.success(`已从本机导入 ${imported} 张包装单，全员可在包装资料批次管理查看`)
      } else if (notify && skipped > 0 && imported === 0 && failed <= 0) {
        ElMessage.info('本机包装单已在服务器存在，已清理本地缓存')
        markImported()
      }
      return { ran: true, imported, skipped, failed }
    } catch (e) {
      if (notify) {
        const msg = e?.response?.data?.message || e?.message
        if (msg) ElMessage.warning(`本机包装单自动导入失败：${msg}`)
      }
      return { ran: true, imported: 0, skipped: 0, failed: true }
    } finally {
      _importInFlight = null
    }
  })()

  return _importInFlight
}
